import { useMemo } from "react";

import { useAppContext } from "./useAppContext";
import { parseAddress, toStateAbbreviation } from "@/shared/utils/addressUtils";
import { useCanonicalAddress } from "@/shared/hooks/useCanonicalAddress";
import { useCountyFromZip } from "@/shared/hooks/useCountyFromZip";
import { usePlaceOfDeath } from "@/shared/hooks/usePlaceOfDeath";

import type { Database } from "@/shared/types/database.types";
import type { PersonWithEncryption } from "@/shared/types/encryption-extensions";

type PersonData = Database["public"]["Tables"]["estates"]["Row"];

export interface DeceasedUserData {
  // Primary data source
  personData: PersonData | null;

  // Computed values with fallbacks
  firstName: string;
  lastName: string;
  displayName: string;
  fullName: string;
  surName: string;

  // Image data
  profileImageUrl: string | null;

  // Status
  isLoading: boolean;
  hasPersonData: boolean;

  // Functions
  refetch: () => void;
  ssnEncrypted: string | null;
  deceasedUserId: number | null;
  supabaseData: PersonData | null;
  state: string | null;
  zipCode: string | null;
  county: string | null;
  city: string | null;
  address: string | null;
  apartment: string | null;
  phoneNumber: string | null;
  email: string | null;
  dateOfBirth: string | null;
  dateOfDeath: string | null;
  placeOfDeath: string | null;
  funeralHomeReported: boolean;
  marriageDate: string | null;
  marriagePlace: string | null;
}

export function useDeceasedUserData(): DeceasedUserData {
  const { person, isLoading, refetch } = useAppContext();

  const { data: canonical, isLoading: isCanonicalLoading } = useCanonicalAddress(
    person?.estate_id ?? undefined,
    "domicile",
  );

  const { data: placeOfDeath } = usePlaceOfDeath(person?.estate_id ?? undefined);

  const addressParts = useMemo(() => {
    if (canonical) {
      return {
        streetAddress: canonical.street1,
        apartment: canonical.street2 ?? "",
        city: canonical.city ?? "",
        state: canonical.state ?? "",
        zipCode: canonical.zip,
      };
    }
    return parseAddress(person?.location ?? "");
  }, [canonical, person?.location]);

  const zipCode = addressParts.zipCode || null;

  const { data: countyFromZip, isLoading: isCountyLoading } = useCountyFromZip(zipCode);

  // Memoize the computed values to prevent infinite re-renders
  return useMemo(() => {
    // Use the person data from context
    const effectiveData = person;

    // Compute values with fallbacks
    // In development, provide test data if no person exists
    const isDev = import.meta.env.DEV;
    const firstName = effectiveData?.first_name || (isDev ? "John" : "");
    const lastName = effectiveData?.last_name || (isDev ? "Doe" : "");
    const displayName = effectiveData?.nickname || firstName || (isDev ? "John" : "");

    // Use full_name if available, otherwise combine first and last name
    const fullName = effectiveData?.full_name || `${firstName} ${lastName}`.trim() || "";

    // Use main_image_url if available
    const profileImageUrl = effectiveData?.main_image_url || null;

    const extended = effectiveData as PersonWithEncryption | null;
    const marriageInfo = extended?.marriage_info as Record<string, string> | null;

    return {
      personData: effectiveData as PersonData | null,
      firstName,
      lastName,
      displayName,
      surName: lastName,
      fullName,
      profileImageUrl,
      isLoading: isLoading || isCountyLoading || isCanonicalLoading,
      hasPersonData: !!person,
      refetch,
      // Additional fields expected by the interface
      ssnEncrypted: extended?.ssn_encrypted || null,
      deceasedUserId: effectiveData?.estate_id || null,
      supabaseData: effectiveData,
      state: toStateAbbreviation(addressParts.state) || null,
      zipCode: addressParts.zipCode || null,
      county: countyFromZip ?? null,
      city: addressParts.city || null,
      address: addressParts.streetAddress || null,
      apartment: addressParts.apartment || null,
      phoneNumber: null,
      email: null,
      dateOfBirth: effectiveData?.date_of_birth || null,
      dateOfDeath: effectiveData?.date_of_death || null,
      placeOfDeath:
        toStateAbbreviation(placeOfDeath?.state ?? "") || extended?.place_of_death || null,
      funeralHomeReported: extended?.funeral_home_reported ?? false,
      marriageDate: marriageInfo?.marriage_date || null,
      marriagePlace: marriageInfo?.place || null,
    };
  }, [
    person,
    isLoading,
    isCountyLoading,
    isCanonicalLoading,
    refetch,
    countyFromZip,
    addressParts,
    placeOfDeath,
  ]);
}
